import { FormEvent, useCallback } from 'react';

import styled from 'styled-components';
import { noop } from 'lodash';
import { useQuoteBuilderView } from '@paradigm/features/src/unified-rfqs/ui/grfq/quote-builder/view';
import { useQuoteBuilderSubmit } from '@paradigm/features/src/unified-rfqs/ui/grfq/quote-builder/submit-context';
import { CreateQuoteReport } from '@paradigm/features/src/unified-rfqs/ui/submit-quote';
import { useSubmitted } from '@paradigm/features/src/forms/submitted-context';

import { useQuoteBuilderActions } from '@paradigm/features/src/unified-rfqs/ui/grfq/quote-builder/form-context';
import { useGrfqRfq } from '@paradigm/features/src/unified-rfqs/ui/rfq-context';
import ArrowsLeftRight from '@paradigm/design-system/src/assets/ArrowsLeftRight';
import { paradigmLightBlue } from '@paradigm/design-system/src/colors';
import { usePriceFormatterFn } from '@paradigm/features/src/unified-rfqs/ui/hooks/useFormattedPrice';
import Button from '@paradigm/design-system/src/components/Button';

import useKeybinding from '#/hooks/useKeybinding';
import { MOBILE_BREAKPOINT } from '#/containers/layout/constants';
import BasePostOnlySwitch from '#/components/PostOnlySwitch';
import { toast } from '#/components/UnifiedRfqs/toast';
import { useTrackAction } from '@paradigm/features/src/analytics';
import QuoteTimerSelect from './QuoteTimerSelect';
import QuantityField from './QuantityField';
import PriceField from './PriceField';
import { useCreatedQuoteToaster } from './useCreatedQuoteToaster';

export default function QuoteBuilderForm() {
  const { isPostOnly, isValid, side, quantity } = useQuoteBuilderView();
  const isBuySide = side === 'BUY';
  const rfq = useGrfqRfq();

  const { setSubmit } = useSubmitted();
  const { submitQuote, isLoading } = useQuoteBuilderSubmit();
  const formatPrice = usePriceFormatterFn({
    side,
    productCode: rfq.product_codes[0],
    description: rfq.description,
    rfqType: 'grfq',
  });
  const { changePrice, changeQuantity, toggleSide, changeIsPostOnly } =
    useQuoteBuilderActions();

  const { toastCreatedQuote } = useCreatedQuoteToaster(rfq.id);
  const trackAction = useTrackAction();
  const handleSubmit = useCallback(
    (event?: FormEvent) => {
      trackAction('submit quote');
      event?.preventDefault();
      setSubmit(true);

      if (!isValid) return;

      submitQuote((res) => {
        if (!res.ok) {
          toast(res.message);
          return;
        }

        if (!res.data.response.ok) {
          toastFailedQuote(res.data);
          return;
        }

        toastCreatedQuote(
          rfq,
          res.data.response.data,
          formatPrice,
          quantity.value,
        );

        changeQuantity('');
        changePrice('');
      });
    },
    [
      trackAction,
      setSubmit,
      isValid,
      submitQuote,
      toastCreatedQuote,
      rfq,
      formatPrice,
      quantity.value,
      changeQuantity,
      changePrice,
    ],
  );

  useKeybinding('Enter', () => handleSubmit(), noop);

  const handleSwapSide = useCallback(() => {
    trackAction('swap side', { side });
    toggleSide();
  }, [side, toggleSide, trackAction]);

  const handleChangePostOnly = useCallback(() => {
    trackAction('toggle post only', { isPostOnly });
    changeIsPostOnly(!isPostOnly);
  }, [changeIsPostOnly, isPostOnly, trackAction]);

  return (
    <form
      onSubmit={handleSubmit}
      css="height: 100%; display: flex; flex-direction: column;"
    >
      <PostOnlySwitch isPostOnly={isPostOnly} onChange={handleChangePostOnly} />
      <FieldContainer>
        <QuoteTimerSelect />
      </FieldContainer>
      <FieldContainer>
        <QuantityField />
      </FieldContainer>
      <FieldContainer>
        <PriceField />
      </FieldContainer>
      <ButtonsContainer>
        <SwapSideButton onClick={handleSwapSide} title="Swap side">
          <ArrowsLeftRight />
        </SwapSideButton>
        <SubmitButtonContainer>
          <Button
            type="submit"
            ariaLabel="Place limit order"
            css="width: 116px;"
            isValid={isValid}
            disabled={!isValid}
            isLoading={isLoading}
            theme={isBuySide ? 'primary-green' : 'primary-red'}
          >
            Limit {isBuySide ? 'Buy' : 'Sell'}
          </Button>
        </SubmitButtonContainer>
      </ButtonsContainer>
    </form>
  );
}

function toastFailedQuote(report: CreateQuoteReport) {
  if (report.request.signal?.aborted === true) return;

  const { side } = report.request.quote;
  const isBuySide = side === 'BUY';
  const type = isBuySide ? 'Bid Error' : 'Ask Error';
  toast(`${type}: ${report.response.message}`);
}

const PostOnlySwitch = styled(BasePostOnlySwitch)`
  @media (max-width: ${MOBILE_BREAKPOINT}px) {
    justify-content: flex-start;
  }
`;

const SwapSideButton = styled(Button).attrs({
  type: 'button',
  variant: 'squared',
  size: 'small',
  ariaLabel: 'swap side',
})`
  background-color: ${paradigmLightBlue};
  padding: 0 4px;
  width: 28px;

  @media screen and (max-width: ${MOBILE_BREAKPOINT}px) {
    width: 36px;
    height: 36px;
  }
`;

const ButtonsContainer = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;

  @media screen and (max-width: ${MOBILE_BREAKPOINT}px) {
    margin: 0.5rem 0;
  }
`;

const SubmitButtonContainer = styled.div`
  @media screen and (max-width: ${MOBILE_BREAKPOINT}px) {
    button {
      height: 36px;
      border-radius: 18px;
      font-size: 1rem;
    }
  }
`;

const FieldContainer = styled.div`
  @media screen and (max-width: ${MOBILE_BREAKPOINT}px) {
    label {
      font-size: 1rem;
    }
  }
`;
